import { atom } from "jotai";
import { v4 as uuidv4 } from "uuid";

import { PatronEditBooking } from "../../../types";
import { currentBookingAtom } from "../../stores";
import { normalizeGroupHosts } from "../setHostIdAndIndexOnRoundAtom/setHostIdAndIndexOnRoundAtom";

const SUPPORTED_BOOKING_TYPES: ReadonlyArray<PatronEditBooking.BookingType> = [
  "Golf",
  "Duration",
  "Lesson",
];

const isGuestLikeClientType = (
  clientType: PatronEditBooking.RoundPlayer["clientType"]
) => {
  return (
    clientType === "guest" ||
    clientType === "unknownGuest" ||
    clientType === "newGuest" ||
    clientType === "unknownCustomer"
  );
};

const ensureNewGuestIdentifier = (
  player: PatronEditBooking.RoundPlayer
): PatronEditBooking.RoundPlayer => {
  if (player.clientType !== "newGuest") {
    return player;
  }

  return {
    ...player,
    id: player.id ?? `guest:${uuidv4()}`,
  };
};

const getLocalIdPrefix = (
  player: PatronEditBooking.RoundPlayer
): "GuestRound" | "MemberRound" => {
  return player.clientType === "member" || player.clientType === "memberHold"
    ? "MemberRound"
    : "GuestRound";
};

const getNextRoundIndex = (
  existingRounds: ReadonlyArray<{ index: number }>
) => {
  return (
    existingRounds.reduce(
      (highestIndex, round) => Math.max(highestIndex, round.index),
      -1
    ) + 1
  );
};

const getBookingIndexForNewRound = (
  rounds: PatronEditBooking.Round[],
  groupCapacity?: number | null,
  requestGroupCount?: number | null,
  requestedBookingIndex?: number
): number | null | undefined => {
  const indexedRounds = rounds.filter(
    (round): round is PatronEditBooking.Round & { bookingIndex: number } =>
      round.bookingIndex != null
  );
  if (indexedRounds.length === 0) return undefined;

  const groupCount =
    requestGroupCount ??
    Math.max(...indexedRounds.map((round) => round.bookingIndex)) + 1;
  const capacity = groupCapacity ?? Number.POSITIVE_INFINITY;
  const groupCounts = Array.from({ length: groupCount }, (_, bookingIndex) => ({
    bookingIndex,
    count: indexedRounds.filter(
      (round) => round.bookingIndex === bookingIndex && !round.isNonPlayingHost
    ).length,
  }));
  const availableGroups = groupCounts.filter((group) => group.count < capacity);

  if (requestedBookingIndex != null) {
    return availableGroups.some(
      (group) => group.bookingIndex === requestedBookingIndex
    )
      ? requestedBookingIndex
      : null;
  }

  return (
    availableGroups.reduce(
      (leastPopulated, group) =>
        group.count < leastPopulated.count ? group : leastPopulated,
      availableGroups[0] ?? null
    )?.bookingIndex ?? null
  );
};

const buildGolfRound = (
  player: PatronEditBooking.RoundPlayer,
  existingRounds: PatronEditBooking.GolfRound[]
): PatronEditBooking.GolfRound => {
  const templateRound = existingRounds[0];
  const playerWithId = ensureNewGuestIdentifier(player);
  const localId = `${getLocalIdPrefix(playerWithId)}:${uuidv4()}`;
  const ownerRound = existingRounds.find((round) => round.isOwner);
  const roundWithTransportation = existingRounds.find(
    (round) => !!round.transportationType
  );
  const roundWithRoundRate = existingRounds.find((round) => !!round.roundRate);
  const roundWithBookedRate = existingRounds.find(
    (round) => !!round.bookedRate
  );

  const courseLayout = templateRound?.courseLayout ?? null;
  const transportationType =
    ownerRound?.transportationType ??
    templateRound?.transportationType ??
    roundWithTransportation?.transportationType ??
    null;
  const inheritedRoundRate =
    templateRound?.roundRate ?? roundWithRoundRate?.roundRate ?? null;
  const inheritedBookedRate =
    templateRound?.bookedRate ?? roundWithBookedRate?.bookedRate ?? null;
  const shouldNotInheritRates = isGuestLikeClientType(playerWithId.clientType);

  return {
    localId,
    serverId: null,
    index: getNextRoundIndex(existingRounds),
    player: playerWithId,
    ...(playerWithId.clientType === "newGuest"
      ? { hostId: playerWithId.hostId || null }
      : {}),
    type: "Golf",
    courseLayout,
    transportationType,
    roundRate: shouldNotInheritRates ? null : inheritedRoundRate,
    bookedRate: shouldNotInheritRates ? null : inheritedBookedRate,
  };
};

const buildDurationRound = (
  player: PatronEditBooking.RoundPlayer,
  existingRounds: PatronEditBooking.DurationRound[]
): PatronEditBooking.DurationRound => {
  const templateRound = existingRounds[0];
  const playerWithId = ensureNewGuestIdentifier(player);
  const localId = `${getLocalIdPrefix(playerWithId)}:${uuidv4()}`;
  const roundWithRate = existingRounds.find((round) => !!round.rate);
  const inheritedRate = templateRound?.rate ?? roundWithRate?.rate ?? null;
  const shouldNotInheritRate = isGuestLikeClientType(playerWithId.clientType);

  return {
    localId,
    serverId: null,
    index: getNextRoundIndex(existingRounds),
    player: playerWithId,
    ...(playerWithId.clientType === "newGuest"
      ? { hostId: playerWithId.hostId || null }
      : {}),
    type: "Duration",
    rate: shouldNotInheritRate ? null : inheritedRate,
  };
};

type AddPrivatePlayerPayload =
  | PatronEditBooking.RoundPlayer
  | {
      player: PatronEditBooking.RoundPlayer;
      bookingIndex?: number;
    };

export const addPrivatePlayerAtom = atom(
  null,
  (get, set, payload: AddPrivatePlayerPayload) => {
    const currentBooking = get(currentBookingAtom);
    if (!currentBooking) {
      return;
    }

    const { player, bookingIndex: requestedBookingIndex } =
      "player" in payload ? payload : { player: payload };

    if (
      player.id &&
      currentBooking.rounds.some((round) => round.player.id === player.id)
    )
      return;

    if (!SUPPORTED_BOOKING_TYPES.includes(currentBooking.type)) {
      return;
    }

    if (currentBooking.type === "Golf") {
      const golfRounds = currentBooking.rounds.filter(
        (round): round is PatronEditBooking.GolfRound => round.type === "Golf"
      );

      const bookingIndex = getBookingIndexForNewRound(
        currentBooking.rounds,
        currentBooking.groupCapacity,
        currentBooking.requestGroupCount,
        requestedBookingIndex
      );
      if (bookingIndex === null) return;

      const newRound = buildGolfRound(player, golfRounds);
      const indexedNewRound =
        bookingIndex == null ? newRound : { ...newRound, bookingIndex };
      const updatedRounds: PatronEditBooking.GolfRound[] = [
        ...golfRounds,
        indexedNewRound,
      ];
      const rounds =
        player.clientType === "newGuest" && player.hostId
          ? normalizeGroupHosts(
              updatedRounds,
              currentBooking.isPrivate &&
                currentBooking.status === "DRAFT" &&
                updatedRounds.every((round) => round.bookingIndex == null)
            ).sort((a, b) => a.index - b.index)
          : updatedRounds;

      set(currentBookingAtom, {
        ...currentBooking,
        rounds,
      });
      return;
    }

    const durationRounds = currentBooking.rounds.filter(
      (round): round is PatronEditBooking.DurationRound =>
        round.type === "Duration"
    );

    const bookingIndex = getBookingIndexForNewRound(
      currentBooking.rounds,
      currentBooking.groupCapacity,
      currentBooking.requestGroupCount,
      requestedBookingIndex
    );
    if (bookingIndex === null) return;

    const newRound = buildDurationRound(player, durationRounds);
    const indexedNewRound =
      bookingIndex == null ? newRound : { ...newRound, bookingIndex };
    const updatedRounds: PatronEditBooking.DurationRound[] = [
      ...durationRounds,
      indexedNewRound,
    ];

    set(currentBookingAtom, {
      ...currentBooking,
      rounds: updatedRounds,
    });
  }
);
